// react-range lib
// https://github.com/tajo/react-range

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { getTrackBackground, Range } from 'react-range';

import { getText } from 'shared/utils/Text';

import styles from './index.module.scss';

const Slider = ({
  captionMax,
  captionMin,
  className,
  description,
  disabled = false,
  max = 100,
  min = 0,
  onChange,
  step = 1,
  title,
  values,
}) => {
  const sortedValues = useMemo(
    () => values && [...values].sort((a, b) => a - b),
    [values],
  );

  if (!sortedValues) {
    return null;
  }

  return (
    <div className={classnames(styles.wrapper, className)}>
      <div className={styles.header}>
        {title && <div className={styles.title}>{title}</div>}
        {description && <div className={styles.description}>{description}</div>}
      </div>

      <div className={styles.container}>
        <Range
          disabled={disabled}
          values={sortedValues}
          step={step}
          min={min}
          max={max}
          onChange={onChange}
          renderTrack={({ props, children }) => (
            <div
              onMouseDown={props.onMouseDown}
              onTouchStart={props.onTouchStart}
              className={styles.track}
              style={props.style}
            >
              <div
                ref={props.ref}
                className={styles.range}
                style={{
                  background: getTrackBackground({
                    values: sortedValues,
                    colors: [
                      '#e5e5e5',
                      disabled ? '#b1b1b1' : '#09f',
                      '#e5e5e5',
                    ],
                    min,
                    max,
                  }),
                }}
              >
                {children}
              </div>
            </div>
          )}
          renderThumb={({ index, props, isDragged }) => {
            const isMax = index === 1;
            const caption = isMax
              ? captionMax || 'input-range-max'
              : captionMin || 'input-range-min';
            const value = sortedValues[index].toFixed(0);

            const isFirst = props['aria-valuenow'] === min;
            const isLast = props['aria-valuenow'] === max;

            return (
              <div
                className={classnames(styles.thumbContainer, {
                  [styles.disabled]: disabled,
                })}
                {...props}
              >
                {isDragged && (
                  <div className={styles.thumb}>
                    <div
                      className={classnames(styles.inner, {
                        [styles.first]: isFirst,
                        [styles.last]: isLast,
                      })}
                    >
                      {getText(caption, value)}
                    </div>
                  </div>
                )}
              </div>
            );
          }}
        />

        <div className={styles.points}>
          {Array.from({
            length: max - min + 1,
          }).map((_, idx) => {
            const s = min + idx * step;

            return <span key={s}>{s}</span>;
          })}
        </div>
      </div>
    </div>
  );
};

Slider.propTypes = {
  captionMax: PropTypes.string,
  captionMin: PropTypes.string,
  className: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  step: PropTypes.number,
  title: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default Slider;
